import { Icon, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { formatSpendBucket, getDualSpendDisplay } from "../lib/dual-spend-display"

interface SpendBucketProps {
  /** Feature and sub-feature of the opportunity, naming the bucket of the secondary share. */
  feature?: string | null
  subFeature?: string | null
}

export function DualSpendValue({
  perDataset,
  totalCreatives,
  decimals = 1,
  className,
  feature,
  subFeature,
}: {
  perDataset: number | null
  totalCreatives: number | null
  decimals?: number
  className?: string
} & SpendBucketProps) {
  const display = getDualSpendDisplay(perDataset, totalCreatives, decimals)

  return (
    <span className={className}>
      <span className="block font-mono tabular-nums text-sm text-muted-foreground">
        {display.primary}
      </span>
      {display.secondaryValue && (
        <WithinBucketLine value={display.secondaryValue} feature={feature} subFeature={subFeature} />
      )}
    </span>
  )
}

export function DualSpendValueWithTooltip({
  perDataset,
  totalCreatives,
  decimals = 1,
  feature,
  subFeature,
}: {
  perDataset: number | null
  totalCreatives: number | null
  decimals?: number
} & SpendBucketProps) {
  const { t } = useTranslation()
  const display = getDualSpendDisplay(perDataset, totalCreatives, decimals)
  const naTooltip = t("opportunities.drawer.spendUnavailableTooltip")

  if (!display.unavailable) {
    return (
      <DualSpendValue
        perDataset={perDataset}
        totalCreatives={totalCreatives}
        decimals={decimals}
        feature={feature}
        subFeature={subFeature}
      />
    )
  }

  return (
    <span>
      <span className="inline-flex items-center gap-1 font-mono text-sm text-muted-foreground">
        {display.primary}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help" tabIndex={0} aria-label={naTooltip}>
              <Icon name="info" size="xs" className="text-muted-foreground/50" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">{naTooltip}</TooltipContent>
        </Tooltip>
      </span>
      {display.secondaryValue && (
        <WithinBucketLine value={display.secondaryValue} feature={feature} subFeature={subFeature} />
      )}
    </span>
  )
}

function WithinBucketLine({ value, feature, subFeature }: { value: string } & SpendBucketProps) {
  return (
    <span className="block text-[10px] leading-tight text-muted-foreground/70">
      <WithinBucketText value={value} feature={feature} subFeature={subFeature} />
    </span>
  )
}

/** "29.8% within Narrative › Painpoint" — the secondary share, named by its feature + sub-feature bucket. */
export function WithinBucketText({ value, feature, subFeature }: { value: string } & SpendBucketProps) {
  const { t } = useTranslation()
  const bucket = formatSpendBucket(feature, subFeature)

  return bucket
    ? t("opportunities.spend.withinBucketLabel", { value, bucket, defaultValue: "{{value}} within {{bucket}}" })
    : t("opportunities.spend.withinSubFeatureLabel", { value, defaultValue: "{{value}} within this sub-feature" })
}
